import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useExternalItemsControllerCreateRequest,
  useExternalItemsControllerGetMyRequests,
  getExternalItemsControllerGetMyRequestsQueryKey,
  type CreateSupplierItemRequestDto,
  type SupplierItemRequestSummaryDto,
} from "@semo/api-contracts";

/**
 * Hook to manage supplier item requests
 * Wraps Orval-generated hooks with domain logic
 */
export const useSupplierRequests = (
  options: { page?: number; limit?: number } = {},
) => {
  const queryClient = useQueryClient();
  const { page = 1, limit = 20 } = options;

  const { mutateAsync: createRequest, isPending: isCreating } =
    useExternalItemsControllerCreateRequest({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getExternalItemsControllerGetMyRequestsQueryKey(),
          });
          toast.success("물품 등록 요청이 제출되었습니다.");
        },
        onError: () => {
          toast.error("요청 제출에 실패했습니다.");
        },
      },
    });

  const {
    data: requestsData,
    isLoading,
    isFetching,
    error,
  } = useExternalItemsControllerGetMyRequests(
    {
      limit,
      offset: (page - 1) * limit,
    },
    {
      query: {
        staleTime: 1000 * 60 * 5,
      },
    },
  );

  const requests: SupplierItemRequestSummaryDto[] = (
    requestsData?.data?.items || []
  ).map((item) => ({
    ...item,
    unit: typeof item.unit === "string" ? item.unit : null,
    updatedAt: typeof item.updatedAt === "string" ? item.updatedAt : null,
  })) as unknown as SupplierItemRequestSummaryDto[];
  const pagination = requestsData?.data?.meta;

  return {
    requests,
    pagination,
    isLoading,
    isFetching,
    error,
    createRequest: (data: CreateSupplierItemRequestDto) =>
      createRequest({ data }),
    isCreating,
  };
};
